export const SUBSCRIPTIONS_COLLECTION = 'subscriptions';
export const LEGACY_SUBSCRIPTIONS_COLLECTION = 'package_subscriptions';

const PLAN_NAMES = {
    basic: 'Pretplatnički paket: BASIC',
    'better-value': 'Pretplatnički paket: BETTER VALUE',
    premium: 'Pretplatnički paket: PREMIUM',
    'premium-public-sector': 'Pretplatnički paket: PREMIUM PUBLIC',
    'revicon-club': 'Pretplatnički paket: REVICON CLUB',
};

const FILTER_PLAN_CODES = {
    basic: ['basic'],
    bv: ['better-value'],
    premium: ['premium'],
    premiumjs: ['premium-public-sector'],
    rc: ['revicon-club'],
};

const text = value => typeof value === 'string' ? value.trim() : '';

const normalizedCustomerTypeText = value => text(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

export const normalizeCustomerType = value => {
    if (value === true) return 'person';
    if (value === false) return 'company';

    const normalized = normalizedCustomerTypeText(value);
    if (['person', 'fizicko lice'].includes(normalized)) return 'person';
    if (['company', 'kompanija', 'pravno lice'].includes(normalized)) return 'company';
    return 'unknown';
};

export const resolveCustomerType = (value, company) => {
    if (normalizeCustomerType(company) === 'person') return 'person';
    const normalizedType = normalizeCustomerType(value);
    if (normalizedType !== 'unknown') return normalizedType;
    return text(company) ? 'company' : 'person';
};

export const companyNameForCustomer = (company, customerType) =>
    normalizeCustomerType(customerType) === 'person' ? '' : text(company);

export const customerTypeLabel = value => ({
    person: 'Fizičko lice',
    company: 'Kompanija',
    unknown: 'Nepoznato',
}[normalizeCustomerType(value)]);

export const planCodeFromValue = value => {
    const normalized = text(value)
        .replace(/^pretplatni(?:c|č)ki paket\s*:\s*/i, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    const aliases = {
        ba: 'basic',
        'beeter-value': 'better-value',
        'better-values': 'better-value',
        'premium-js': 'premium-public-sector',
        'premium-public': 'premium-public-sector',
        'public-sector': 'premium-public-sector',
        'revicon-club': 'revicon-club',
    };

    return aliases[normalized] || normalized;
};

const LEGACY_PACKAGE_PILL_PRESENTATIONS = {
    basic: { background: '#00afda', foreground: '#17313a' },
    'better-value': { background: '#a83c77', foreground: '#ffffff' },
    premium: { background: '#276bcf', foreground: '#ffffff' },
    'premium-public-sector': { background: '#2bafbd', foreground: '#17313a' },
    'revicon-club': { background: '#c92026', foreground: '#ffffff' },
};

const PACKAGE_PILL_PRESENTATIONS_2027 = {
    basic: { background: '#e0e0e0', foreground: '#262626' },
    'better-value': { background: '#a55098', foreground: '#ffffff' },
    premium: { background: '#0e62c1', foreground: '#ffffff' },
    'premium-public-sector': { background: '#21adbf', foreground: '#17313a' },
    'revicon-club': { background: '#262626', foreground: '#ffffff' },
};

export const packagePillPresentation = (value, year) => {
    const palette = String(year) === '2027'
        ? PACKAGE_PILL_PRESENTATIONS_2027
        : LEGACY_PACKAGE_PILL_PRESENTATIONS;

    return palette[planCodeFromValue(value)] || {
    background: '#19a9d5',
    foreground: '#17313a',
    };
};

const packageNameFor = data => {
    const snapshotName = text(data.invoice?.snapshot?.packageName || data.invoice?.packageName);
    if (snapshotName) return `Pretplatnički paket: ${snapshotName}`;

    const legacyName = text(data.legacy?.packageName);
    if (legacyName) return legacyName;

    const code = planCodeFromValue(data.planCode);
    return PLAN_NAMES[code] || text(data.planCode);
};

export const normalizeSubscriptionDocument = (id, data = {}) => {
    const customer = data.customerSnapshot || {};
    const legacyDocumentId = text(data.legacy?.documentId || data.invoice?.legacyPdfId);
    const customerType = resolveCustomerType(
        customer.customerType || customer.customer_type || data.customerType || data.customer_type,
        customer.company
    );

    return {
        id: String(id),
        sourceCollection: SUBSCRIPTIONS_COLLECTION,
        data: {
            user: text(customer.name),
            company: companyNameForCustomer(customer.company, customerType),
            customer_type: customerType,
            description: text(data.description || data.admin?.description || data.legacy?.unknownFields?.description),
            email: text(customer.email || data.emailNormalized),
            address: text(customer.address),
            city: text(customer.city),
            phone: text(customer.phone),
            zip: text(customer.zip),
            date: data.createdAt || data.legacy?.sourceDate || '',
            paid: data.paid === true,
            paid_date: data.paidAt || data.payment?.paidAt || data.legacy?.unknownFields?.paid_date || '',
            invoiceId: text(data.invoice?.invoiceId),
            package_name: packageNameFor(data),
            package_code: planCodeFromValue(data.planCode || data.legacy?.packageName),
            year: data.planYear === undefined || data.planYear === null ? '' : String(data.planYear),
            isNewUser: data.isNewUser === true || data.legacy?.unknownFields?.isNewUser === true,
            pdfId: text(data.invoice?.legacyPdfId) || String(id),
            legacyDocumentId,
            status: text(data.status),
        },
    };
};

export const normalizeLegacySubscriptionDocument = (id, data = {}) => {
    const customerType = resolveCustomerType(data.fizicko_lice, data.company);

    return {
        id: String(id),
        sourceCollection: LEGACY_SUBSCRIPTIONS_COLLECTION,
        data: {
            ...data,
            user: text(data.user),
            company: companyNameForCustomer(data.company, customerType),
            customer_type: customerType,
            description: text(data.description),
            email: text(data.email),
            address: text(data.address),
            city: text(data.city),
            phone: text(data.phone),
            zip: text(data.zip),
            invoiceId: text(data.invoiceId),
            package_name: text(data.package_name),
            package_code: planCodeFromValue(data.package_name),
            year: data.year === undefined || data.year === null ? '' : String(data.year),
            paid: data.paid === true,
            pdfId: String(id),
            legacyDocumentId: String(id),
        },
    };
};

export const matchesPackageFilter = (row, packageFilter) => {
    if (!packageFilter) return true;
    const acceptedCodes = FILTER_PLAN_CODES[packageFilter] || [];
    return acceptedCodes.includes(planCodeFromValue(row?.data?.package_code || row?.data?.package_name));
};

export const calculatePackageSubscriptionTotals = (rows = []) => {
    const plans = {
        basic: { total: 0, paid: 0 },
        'better-value': { total: 0, paid: 0 },
        premium: { total: 0, paid: 0 },
        'premium-public-sector': { total: 0, paid: 0 },
        'revicon-club': { total: 0, paid: 0 },
    };
    let paid = 0;
    let revicon = 0;

    rows.forEach(row => {
        const data = row?.data || {};
        const isPaid = data.paid === true;
        const email = text(data.email).toLowerCase();
        const planCode = planCodeFromValue(data.package_code || data.package_name);

        if (isPaid) paid += 1;
        if (email.endsWith('@revicon.info') || email.endsWith('@revicon.ba')) revicon += 1;
        if (!plans[planCode]) return;

        plans[planCode].total += 1;
        if (isPaid) plans[planCode].paid += 1;
    });

    return { paid, revicon, plans };
};

export const mergePackageSubscriptionRows = (primaryRows = [], legacyRows = [], packageFilter = '') => {
    const legacyByDocumentId = new Map(legacyRows.map(row => [String(row.id), row]));
    const legacyByInvoiceId = new Map(legacyRows
        .filter(row => row.data.invoiceId)
        .map(row => [row.data.invoiceId, row]));
    const enrichedPrimaryRows = primaryRows.map(row => {
        if (row.data.customer_type !== 'unknown') return row;
        const legacyRow = legacyByDocumentId.get(String(row.data.legacyDocumentId || row.id)) ||
            legacyByInvoiceId.get(row.data.invoiceId);
        if (!legacyRow || legacyRow.data.customer_type === 'unknown') return row;
        return { ...row, data: { ...row.data, customer_type: legacyRow.data.customer_type } };
    });
    const primaryDocumentIds = new Set();
    const primaryInvoiceIds = new Set();

    enrichedPrimaryRows.forEach(row => {
        primaryDocumentIds.add(String(row.id));
        if (row.data.legacyDocumentId) primaryDocumentIds.add(String(row.data.legacyDocumentId));
        if (row.data.invoiceId) primaryInvoiceIds.add(row.data.invoiceId);
    });

    const filteredPrimary = enrichedPrimaryRows.filter(row => matchesPackageFilter(row, packageFilter));
    const fallbackRows = legacyRows.filter(row =>
        matchesPackageFilter(row, packageFilter) &&
        !primaryDocumentIds.has(String(row.id)) &&
        (!row.data.invoiceId || !primaryInvoiceIds.has(row.data.invoiceId))
    );

    return [...filteredPrimary, ...fallbackRows];
};
